import { useState, useRef } from 'react';
import { X, UsersRound } from 'lucide-react';
import CrowdLockIcon from '../../assets/images/crowdlock.svg?component';
import ForcedEntryGraphic from '../../assets/images/forced_entry.svg?component';
import TopBorder from '../../assets/images/top_border_forced.svg?component';
import BottomBorder from '../../assets/images/bottom_border_forced.svg?component';
import Graphic from '../../assets/images/graphic_forced_entry.avif';

export default function CrowdLockIntroModal({ open, onClose }) {
	const [step, setStep] = useState(1);

	const MIN_STEP = 1;
	const MAX_STEP = 3;
	const SWIPE_THRESHOLD = 50;

	const renderPagination = () => (
		<div className="mb-3 flex justify-center gap-2">
			{[1, 2, 3].map((i) => (
				<div
					key={i}
					className={`h-0.5 w-10 rounded-[1px] ${step === i ? 'bg-[#ACC0F6]' : 'bg-[#2D3A6C]'}`}
				/>
			))}
		</div>
	);

	const startXRef = useRef(null);

	const handleTouchStart = (e) => {
		startXRef.current = e.touches[0].clientX;
	};

	const handleTouchEnd = (e) => {
		if (startXRef.current === null) return;

		const selection = window.getSelection();
		if (selection && selection.toString().length > 0) {
			startXRef.current = null;
			return;
		}

		const endX = e.changedTouches[0].clientX;
		const deltaX = endX - startXRef.current;

		if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
			if (deltaX < 0 && step < MAX_STEP) {
				setStep((s) => s + 1); // swipe left
			} else if (deltaX > 0 && step > MIN_STEP) {
				setStep((s) => s - 1); // swipe right
			}
		}

		startXRef.current = null;
	};

	const handleMouseDown = (e) => {
		startXRef.current = e.clientX;
	};

	const handleMouseUp = (e) => {
		if (startXRef.current === null) return;

		const selection = window.getSelection();
		if (selection && selection.toString().length > 0) {
			startXRef.current = null;
			return;
		}

		const deltaX = e.clientX - startXRef.current;

		if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
			if (deltaX < 0 && step < MAX_STEP) {
				setStep((s) => s + 1);
			} else if (deltaX > 0 && step > MIN_STEP) {
				setStep((s) => s - 1);
			}
		}

		startXRef.current = null;
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
			<div
				className="relative w-[345px] h-[442px] rounded-md bg-gradient-to-b from-[#2D3A6C] to-[#1E2748] p-4 shadow-xl"
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
			>
				{/* Close */}
				<button onClick={onClose} className="absolute right-3 top-3 text-white/60 hover:text-white">
					<X size={18} />
				</button>

				{/* STEP 1 */}
				{step === 1 && (
					<>
						<div className="-mt-2 flex items-center justify-center">
							<img src={CrowdLockIcon} alt="Crowd Lock" className="h-[64px] w-[64px]" />
						</div>

						{/* Progress */}
						<div className="mb-2">
							<div>
								<div className="w-[155px] h-2 bg-[#611818]/30 rounded-tl-lg rounded-bl-lg border-l border-t border-b border-[#DA3030] inline-flex flex-col justify-start items-start gap-2.5">
									<div className="w-20 h-2 bg-[#DA3030] rounded-tl-lg rounded-bl-lg border-l border-t border-b border-[#DA3030]" />
								</div>
								<div className="w-[155px] h-2 bg-[#1B744D]/30 rounded-tr-lg rounded-br-lg border-r border-t border-b border-[#14AE5C] inline-flex flex-col justify-start items-end gap-2.5">
									<div className="w-20 h-2 bg-[#14AE5C] rounded-tr-lg rounded-br-lg border-r border-t border-b border-[#14AE5C]" />
								</div>
							</div>
							<div className="flex justify-between text-[11px] text-white/70 mb-1">
								<div className="w-[155px] text-[#E87D7D] flex items-center">
									<UsersRound className="ml-[11px] mr-[4px] h-3 w-3" />
									<span className="mr-[8px] text-sm font-bold">32 </span>
									<span className="text-xs font-medium leading-4">PLAYERS ON NO</span>
								</div>
								<div className="w-[155px] text-[#36E278] flex items-center">
									<UsersRound className="ml-[11px] mr-[4px] h-3 w-3" />
									<span className="mr-[8px] text-sm font-bold">32 </span>
									<span className="text-xs font-medium leading-4">PLAYERS ON YES</span>
								</div>
							</div>
						</div>

						<h4 className="mt-[44px] text-center text-[10px] uppercase leading-4 tracking-wide font-semibold font-['DM_Sans'] text-[#36E278]">
							CROWD LOCK
						</h4>
						<h2 className="flex flex-col text-center text-xl font-semibold text-[#F7F7F8] leading-6 mt-2">
							<span>Beat the crowd or</span>
							<span>get locked out</span>
						</h2>
						<h2 className="mt-[24px] text-center text-[#F7F7F8] text-sm font-bold">
							Race The Crowd
						</h2>

						<p className="mt-2 text-center text-sm text-[#D4D5D8] font-normal mb-6">
							<span>The market </span>
							<span className="font-semibold">LOCKS</span>
							<span> the moment </span>
							<span className="font-semibold">50% of players</span>
							<span> pick the same side. Act fast, or wait for info– but don’t wait too long.</span>
						</p>

						{renderPagination()}

						<button
							onClick={() => setStep(2)}
							className="w-full h-[40px] rounded-full bg-[#2F6BFF] text-sm font-bold text-[#F7F7F8]"
						>
							Next
						</button>
					</>
				)}

				{/* STEP 2 */}
				{step === 2 && (
					<>
						<div className="mb-1 ml-[60px] mr-[60px] flex flex-col justify-center text-center ">
							<img src={TopBorder} alt="Top Border" className="w-[192px] mb-1" />
							<div className="mt-1 mb-1 justify-start text-[#F7F7F8] text-sm font-bold font-['DM_Sans']">
								Forced Entry
							</div>
							<img src={BottomBorder} alt="Bottom Border" className="w-[192px] mt-1" />
						</div>

						<img src={ForcedEntryGraphic} alt="Forced Entry" className="w-[311px]" />

						<h4 className="mt-[12px] text-center text-[10px] uppercase leading-4 tracking-wide font-semibold font-['DM_Sans'] text-[#36E278]">
							CROWD LOCK
						</h4>
						<h2 className="flex flex-col text-center text-xl font-semibold text-[#F7F7F8] leading-6 mt-2">
							<span>Beat the crowd or</span>
							<span>get locked out</span>
						</h2>
						<h2 className="mt-[20px] text-center text-[#F7F7F8] text-sm font-bold">
							Don't Get Forced
						</h2>

						<p className="mt-2 flex flex-col text-center text-sm text-[#D4D5D8] font-normal mb-4">
							<span>If a side fills up before you pick, you are</span>
							<span>automatically FORCED onto</span>
							<span>the remaining side</span>
						</p>

						{renderPagination()}

						<div className="flex gap-3">
							<button
								onClick={() => setStep(1)}
								className="w-[152px] h-[40px] rounded-full border border-[#2667FF] text-sm font-bold text-white"
							>
								Back
							</button>
							<button
								onClick={() => setStep(3)}
								className="w-[152px] h-[40px] rounded-full bg-[#2F6BFF] text-sm font-bold text-white"
							>
								Next
							</button>
						</div>
					</>
				)}

				{/* STEP 3 */}
				{step === 3 && (
					<>
						<div className="mt-[36px] flex justify-center">
							<img src={Graphic} alt="Graphic" className="w-[242px]" />
						</div>

						<h4 className="mt-[44px] text-center text-[10px] uppercase leading-4 tracking-wide font-semibold font-['DM_Sans'] text-[#36E278]">
							CROWD LOCK
						</h4>
						<h2 className="flex flex-col text-center text-xl font-semibold text-[#F7F7F8] leading-6 mt-2">
							<span>Beat the crowd or</span>
							<span>get locked out</span>
						</h2>
						<h2 className="mt-[24px] text-center text-[#F7F7F8] text-sm font-bold">
							Survive the Split
						</h2>

						<p className="mt-2 text-center text-sm text-[#D4D5D8] font-normal mb-4">
							<span>Winners survive to the next market. Losers are</span>
							<span>eliminated. Last one standing wins.</span>
						</p>

						{renderPagination()}

						<div className="flex gap-3">
							<button
								onClick={() => setStep(2)}
								className="w-[152px] h-[40px] rounded-full border border-[#2667FF] text-sm font-bold text-white"
							>
								Back
							</button>
							<button
								onClick={onClose}
								className="w-[152px] h-[40px] rounded-full bg-green-500 text-sm font-bold text-white"
							>
								I’m Ready!
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
